import { DEFAULT_CONFIGS } from '../data/designVariants'
import type { DesignId, DesignMode, DesignVariantConfig, SurfaceKey } from '../types/experience'

/**
 * The wire contract between Campuzon and the configuration service.
 *
 * The client's internal record and the published record are deliberately not
 * the same shape. The wire format is stable, uses seconds (a duration a person
 * reads in an API response should not be a millisecond count), and carries a
 * version so a stale response can be recognised. Everything that is only
 * meaningful inside the browser — the surface enum spelling, the paused-on
 * design — is translated here, in one place, so no screen ever parses a
 * payload and no endpoint change reaches a component.
 *
 *   GET  /api/config/design-variants            public, rendering only
 *   PUT  /api/admin/design-variants/{surface}   authorised publish
 *   GET  /api/admin/design-variants/history     authorised, audited
 */

/** The identifier a surface has on the wire. */
export type WireSurface = 'homepage' | 'housing' | 'discover'

const TO_WIRE: Record<SurfaceKey, WireSurface> = {
  HOME: 'homepage',
  HOUSING_FIND_HOME: 'housing',
  DISCOVER: 'discover',
}

const FROM_WIRE: Record<WireSurface, SurfaceKey> = {
  homepage: 'HOME',
  housing: 'HOUSING_FIND_HOME',
  discover: 'DISCOVER',
}

export function wireSurface(surface: SurfaceKey): WireSurface {
  return TO_WIRE[surface]
}

/** What the public endpoint returns per surface. Rendering configuration only. */
export interface WireConfig {
  mode: DesignMode
  design1Enabled: boolean
  design2Enabled: boolean
  rotationIntervalSeconds: number
  rotationStartDesign: DesignId
  rotationPaused: boolean
  /** What was presenting when rotation was paused, if anything. */
  pausedOn?: DesignId | null
  fallbackDesign: DesignId
  version?: number
  updatedAt?: string
  /**
   * Present only on the administrative read. The public endpoint must not
   * carry it — who published a configuration is not rendering information.
   */
  updatedBy?: string
}

export type WirePayload = Partial<Record<WireSurface, WireConfig>>

const MODES: DesignMode[] = ['DESIGN_1', 'DESIGN_2', 'ROTATE']
const DESIGNS: DesignId[] = ['DESIGN_1', 'DESIGN_2']

/**
 * A payload is treated as a claim to validate, never as evidence. Anything
 * that would leave a surface unable to present is rejected here rather than
 * being handed to a controller that must then cope with it.
 */
function toConfig(surface: SurfaceKey, wire: unknown): DesignVariantConfig | null {
  if (!wire || typeof wire !== 'object') return null
  const value = wire as WireConfig

  if (!MODES.includes(value.mode)) return null
  if (typeof value.design1Enabled !== 'boolean' || typeof value.design2Enabled !== 'boolean') {
    return null
  }
  if (!value.design1Enabled && !value.design2Enabled) return null
  if (!DESIGNS.includes(value.rotationStartDesign)) return null
  if (!DESIGNS.includes(value.fallbackDesign)) return null

  const seconds = Number(value.rotationIntervalSeconds)
  if (!Number.isFinite(seconds) || seconds < 1) return null

  return {
    surface,
    mode: value.mode,
    design1Enabled: value.design1Enabled,
    design2Enabled: value.design2Enabled,
    rotationIntervalMs: Math.round(seconds * 1000),
    rotationStartDesign: value.rotationStartDesign,
    rotationPaused: Boolean(value.rotationPaused),
    pausedOn: DESIGNS.includes(value.pausedOn as DesignId) ? (value.pausedOn as DesignId) : null,
    fallbackDesign: value.fallbackDesign,
    updatedAt: value.updatedAt ?? new Date().toISOString(),
    // The public endpoint withholds this deliberately.
    updatedBy: value.updatedBy ?? 'Configuration service',
  }
}

/** Reads a published payload, keeping only surfaces that are valid. */
export function readPayload(payload: WirePayload | null | undefined): Partial<Record<SurfaceKey, DesignVariantConfig>> {
  const result: Partial<Record<SurfaceKey, DesignVariantConfig>> = {}
  if (!payload || typeof payload !== 'object') return result
  for (const [key, wire] of Object.entries(payload)) {
    const surface = FROM_WIRE[key as WireSurface]
    if (!surface) continue
    const config = toConfig(surface, wire)
    if (config) result[surface] = config
  }
  return result
}

/** The body sent when publishing. Version and stamps belong to the server. */
export function writeConfig(config: DesignVariantConfig): WireConfig {
  return {
    mode: config.mode,
    design1Enabled: config.design1Enabled,
    design2Enabled: config.design2Enabled,
    rotationIntervalSeconds: Math.round(config.rotationIntervalMs / 1000),
    rotationStartDesign: config.rotationStartDesign,
    rotationPaused: config.rotationPaused,
    pausedOn: config.pausedOn,
    fallbackDesign: config.fallbackDesign,
  }
}

/** The configuration a surface uses before any service has answered. */
export function safeDefault(surface: SurfaceKey): DesignVariantConfig {
  return DEFAULT_CONFIGS[surface]
}
